import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";

export const TOKEN_STORAGE_KEY = "token";

// Pulls `token` out of the current URL's query string.
export function extractTokenFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search);
  return params.get("token");
}

export function validateToken(token) {
  if (!token || typeof token !== "string") {
    return { valid: false, reason: "Token is missing." };
  }

  let decoded;
  try {
    decoded = jwtDecode(token);
  } catch (err) {
    return { valid: false, reason: "Token is malformed." };
  }

  if (decoded?.exp && decoded.exp * 1000 < Date.now()) {
    return { valid: false, reason: "Token has expired." };
  }

  return { valid: true, decoded };
}

// Removes the `token` param from the current URL without a page reload
// and without leaving it in browser history.
export function stripTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  params.delete("token");

  const remaining = params.toString();
  const cleanedUrl =
    window.location.pathname +
    (remaining ? `?${remaining}` : "") +
    window.location.hash;

  window.history.replaceState({}, document.title, cleanedUrl);
}

export function useURLToken() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [decoded, setDecoded] = useState(null);

  useEffect(() => {
    const tokenFromUrl = extractTokenFromUrl();

    // No token in the URL — not necessarily an error, caller can fall back
    // to an existing localStorage session (e.g. on a page refresh).
    if (!tokenFromUrl) {
      setStatus("no-token");
      return;
    }

    const { valid, reason, decoded: payload } = validateToken(tokenFromUrl);

    if (!valid) {
      setStatus("error");
      setError(reason);
      // Don't leave a bad token sitting in the URL either.
      stripTokenFromUrl();
      return;
    }

    try {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, tokenFromUrl);
      sessionStorage.setItem("tokenTimestamp", Date.now().toString());
    } catch (err) {
      // e.g. storage disabled / quota exceeded / private browsing restrictions
      setStatus("error");
      setError("Unable to store token: " + err.message);
      return;
    }

    stripTokenFromUrl();
    setDecoded(payload);
    setStatus("success");
  }, []);

  return { status, error, decoded };
}
